<template>
    <div class="cap-container">
        <div class="cap">
            <!-- Путь вместо ссылки «назад»: поисковику видна структура каталога -->
            <Breadcrumbs :items="crumbs" />
            <h1>
                {{ category_name }}
                <span v-if="filterApplied" class="appled-filter">Применен поиск</span>
            </h1>
        </div>
        <div class="cap-controls">
            <!-- Скрыть описания разделов совсем: предпочтение посетителя,
                 запоминается в cookie и действует во всём каталоге -->
            <div v-if="seo_text" class="cap-switch">
                Скрыть описание раздела
                <el-switch v-model="hideIntro" class="ml-2" style="--el-switch-on-color: var(--el-color-primary);" inline-prompt
                    active-text="да" inactive-text="нет" />
            </div>
            <div v-if="$device.isDesktop" class="cap-switch">
                Автоматическое переключение изображений
                <el-switch v-model="autoplay" class="ml-2" style="--el-switch-on-color: var(--el-color-primary);" inline-prompt
                    active-text="вкл" inactive-text="выкл" />
            </div>
        </div>
    </div>

    <!-- Вводный текст раздела из админки: его читают и покупатель, и поисковик.
         На узком экране сворачивается, чтобы не закрывать собой витрину;
         свёрнутость и глобальное скрытие запоминаются в cookie -->
    <CategoryIntro
        v-if="seo_text"
        :text="seo_text"
        :storage-key="category"
        :hidden="hideIntro"
    />

    <!-- Фильтр по категории -->
    <div class="filter_aria">
        <!-- Поиск по категории: -->
        <span class="label_filter">Поиск по категории:</span>
        <CustomSlider v-model="sizeRange" :label="'Размер'" :markPostfix="'см'" :min="minSize" :max="maxSize"
            :minInputWidth="'80px'" :step="1" :minWith="'400px'" />
        <!-- <CustomSlider v-model="priceRange" :label="'Цена'" :markPostfix="'₽'" :min="minPrice" :max="maxPrice"
            :minInputWidth="'95px'" :step="50" :minWith="'440px'" /> -->
        <CustomColorPicker v-model="selectedColors" :categoryId="category_id" />
        <el-button type="info" :icon="Close" @click="resetFilters" aria-hidden="true">Сбросить</el-button>
        <el-button type="info" color="#008001" :icon="Search" @click="searchProducts" aria-hidden="true">Найти</el-button>
    </div>

    <!-- Верхний пейджер -->
    <!-- <el-pagination background v-model:current-page="currentPage" :page-size="pageSize" :total="totalItems"
        layout="prev, pager, next, sizes, total" @size-change="handleSizeChange" @current-change="handleCurrentChange"
        :page-sizes="[5, 10, 25, 50, 100]" style="margin-bottom: 10px;" hide-on-single-page/> -->
    <!-- Отображение продукции paginatedProducts--> 
    <ProductsList :products="products" :autoplay="autoplay" />
    <!-- Верхний пейджер -->
    <!-- <el-pagination background v-model:current-page="currentPage" :page-size="pageSize" :total="totalItems"
        layout="prev, pager, next, sizes, total" @size-change="handleSizeChange" @current-change="handleCurrentChange"
        :page-sizes="[5, 10, 25, 50, 100]" style="margin-top: 10px;" hide-on-single-page/> -->
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { Search, Close } from '@element-plus/icons-vue'
import { itemListLd, jsonLdScript } from '~/utils/jsonLd'
// import { ElPagination } from 'element-plus'

const category = useRoute().params.category
const { category_id, category_name, seo_title, meta_description, seo_text } = await $fetch(
    `/api/category_id_by_alias?alias=${category}`
)
const { min_price, max_price, min_size, max_size } = await $fetch(`/api/mix_max_price_size_by_category_id?categoryId=${category_id}`)

// Товары грузим на сервере: иначе в разметке раздела нет ни названий, ни ссылок
// на товары, и поисковик видит пустую страницу
const { data: categoryProducts } = await useAsyncData(
    `category-products-${category_id}`,
    () => $fetch(`/api/products_by_category_id?id=${category_id}`)
)
const products = ref(categoryProducts.value ?? [])

const filterApplied = ref(false)

// Превращаем в ref для управления фильтрами
const minPrice = ref(min_price)
const maxPrice = ref(max_price)
const minSize = ref(Math.floor(min_size))
const maxSize = ref(Math.floor(max_size))

// Значения фильтров
const priceRange = ref([minPrice.value, maxPrice.value])
const sizeRange = ref([minSize.value, maxSize.value])
const selectedColors = ref({ colors: [], colorSearhType: 'OR' })

// Для пейджинга
// const currentPage = ref(1); // текущая страница
// const pageSize = ref(50);   // количество товаров на странице
// const totalItems = ref(0); // общее количество товаров (это должно быть получено из API)

// // Рассчитываем список товаров для текущей страницы
// const paginatedProducts = computed(() => {
//   const start = (currentPage.value - 1) * pageSize.value;
//   const end = start + pageSize.value;
//   totalItems.value = products.value.length
//   return products.value.slice(start, end);
// });

// // Изменение количества товаров на странице
// const handleSizeChange = (newSize) => {
//   pageSize.value = newSize;
//   // Перезапрос данных, если это необходимо
// };

// // Переход на новую страницу
// const handleCurrentChange = (newPage) => {
//   currentPage.value = newPage;
//   // Перезапрос данных, если это необходимо
// };

// Сохранение и восстановление фильтров для категории
const saveFiltersToLocalStorage = () => {
    const filters = {
        priceRange: priceRange.value,
        sizeRange: sizeRange.value,
        selectedColors: selectedColors.value,
        filterApplied: filterApplied.value,
    };
    localStorage.setItem(`filters_${category_id}`, JSON.stringify(filters));
}

const loadFiltersFromLocalStorage = () => {
    const savedFilters = localStorage.getItem(`filters_${category_id}`);
    if (savedFilters) {
        const { priceRange: savedPriceRange, sizeRange: savedSizeRange, selectedColors: savedColors, filterApplied: savedFilterApplied } = JSON.parse(savedFilters);
        priceRange.value = savedPriceRange;
        sizeRange.value = savedSizeRange;
        selectedColors.value = savedColors;
        filterApplied.value = savedFilterApplied;
    }
}

// Метод для поиска с фильтрами
const searchProducts = async () => {
    const [minSelectedPrice, maxSelectedPrice] = priceRange.value
    const [minSelectedSize, maxSelectedSize] = sizeRange.value
    const selectedColorsData = selectedColors.value; // Получаем объект {colors, colorSearhType}

    if (minSelectedPrice == minPrice.value && maxSelectedPrice == maxPrice.value
        && minSelectedSize == minSize.value && maxSelectedSize == maxSize.value
        && selectedColorsData.colors.length == 0
    ) {
        products.value = await $fetch(`/api/products_by_category_id?id=${category_id}`)
        filterApplied.value = false
    }
    else {
        const filteredProducts = await $fetch(`/api/products_by_filters`, {
            method: 'POST',
            body: {
                category_id,
                min_price: minSelectedPrice,
                max_price: maxSelectedPrice,
                min_size: minSelectedSize,
                max_size: maxSelectedSize,
                colors: selectedColorsData.colors,  // Массив цветов
                colorSearhType: selectedColorsData.colorSearhType // Тип поиска (OR или AND)
            }
        })
        products.value = filteredProducts
        filterApplied.value = true
        saveFiltersToLocalStorage(); // Сохраняем фильтры в localStorage
    }
}

// Метод для сброса фильтров
const resetFilters = async () => {
    priceRange.value = [minPrice.value, maxPrice.value]
    sizeRange.value = [minSize.value, maxSize.value]
    selectedColors.value = { colors: [], colorSearhType: 'OR' }
    products.value = await $fetch(`/api/products_by_category_id?id=${category_id}`)
    filterApplied.value = false
    // Удаляем сохраненные фильтры для категории
    localStorage.removeItem(`filters_${category_id}`);
}

const origin = useRequestURL().origin
const categoryUrl = `${origin}/catalog/${category}`

const crumbs = [
    { name: 'Каталог', to: '/catalog' },
    { name: category_name },
]

// Заголовок и описание для выдачи берём из админки, если заполнены. Запасной
// заголовок годится не всем разделам (та же «Керамика» сделана не из гипса) —
// поэтому его и можно переопределить
const metaTitle =
    String(seo_title ?? '').trim() ||
    `${category_name} — статуэтки и сувениры из гипса`

const metaDescription =
    String(meta_description ?? '').trim() ||
    `Раздел «${category_name}»: ${products.value.length} изделий из художественного гипса собственного производства. Опт и розница, доставка по России.`

useHead({
    title: metaTitle,
    meta: [
        { name: 'description', content: metaDescription },
        { property: 'og:title', content: metaTitle },
        { property: 'og:description', content: metaDescription },
        { property: 'og:url', content: categoryUrl },
    ],
    link: [
        { rel: 'canonical', href: categoryUrl },
    ],
    script: [
        jsonLdScript(
            itemListLd(
                origin,
                products.value.map((p) => ({
                    url: `/catalog/${p.category_alias}/${p.product_alias}`,
                    name: p.product_name,
                }))
            )
        ),
    ],
})

// Превращаем autoplay в ref
const autoplay = ref(true)

// Глобальное «скрыть описания разделов» — в cookie, чтобы сервер сразу рисовал
// нужное состояние (см. useIntroPrefs)
const hideIntro = useHideIntro()

// Логика сохранения состояния в localStorage
onMounted(async () => {
    // Восстанавливаем фильтры при загрузке страницы
    loadFiltersFromLocalStorage();
    // Полный список раздела уже пришёл с сервера — повторный запрос нужен
    // только тому, у кого сохранён фильтр
    if (filterApplied.value) await searchProducts()

    // Восстанавливаем состояние autoplay
    const savedAutoplay = localStorage.getItem('autoplay')
    if (savedAutoplay !== null) {
        autoplay.value = JSON.parse(savedAutoplay)
    }
})

// Отслеживаем изменения состояния autoplay и сохраняем его
watch(autoplay, (newVal) => {
    localStorage.setItem('autoplay', JSON.stringify(newVal))
});
</script>

<style scoped>
.appled-filter {
    color: chocolate;
    font-size: 12px;
    font-weight: 500;
}

.filter_aria {
    /* background-color: aqua; */
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    margin-bottom: 20px;
    row-gap: 15px;
}

.label_filter {
    display: flex;
    align-items: center;
    font-weight: 600;
    white-space: nowrap;
    width: min-content;
    padding-right: 7px;
    margin-right: 7px;
    border-right: 1px solid var(--el-border-color-light);
}

.cap-container {
    display: flex;
    align-items: center;
    /* Вертикальное выравнивание по центру */
    justify-content: space-between;
    /* Пространство между элементами */
}

.cap-controls {
    margin-left: auto;
    /* Переключатели в строку, а при нехватке места перенос на новую */
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 6px 18px;
    font-size: 13px;
    font-weight: 200;
}

.cap-switch {
    white-space: nowrap;
}

.cap {
    flex-direction: row;
}

.back-link {
    font-size: 0.75em;
    color: var(--el-color-primary);
    text-decoration: none;
}

.back-link:hover {
    color: var(--sd-green-text);
}

h1 {
    margin-top: 0;
    margin-bottom: 0;
}
</style>
